import { api } from "../common/utils"
import type {
  FeedbackReason,
  FeedbackTargetType,
  WorkflowOutcome
} from "@bb/agent-contract"

/**
 * Client for bb-admin-agent — the BankingBridge AI agent service.
 *
 * Auth: short-lived HMAC token minted by the monolith
 * (GET /api/v1/staff-agent-token — session-authenticated; administrators get
 * platform-wide staff tokens, bank admins get tokens scoped to their org's
 * brands), sent to the agent service as `x-staff-token`. Tokens live 1 hour;
 * we re-mint before expiry and retry once on 401. The token embeds identity
 * AND scope, so the cache MUST be cleared whenever the session identity
 * changes (logout, masquerade) — see clearStaffAgentTokenCache.
 *
 * The agent service streams chat over POST-based SSE, so requests use fetch
 * (EventSource cannot set headers, and the shared axios instance points at
 * the monolith).
 */

const AGENT_URL =
  process.env.REACT_APP_ADMIN_AGENT_URL ??
  (process.env.NODE_ENV === "development" ? "http://localhost:3010" : "")

const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000

let cachedToken: { token: string; expiresAt: number } | null = null

/**
 * Drops the cached agent token. Called on logout and masquerade transitions —
 * a cached token carries the PREVIOUS identity's role and brand scope for up
 * to an hour otherwise.
 */
export function clearStaffAgentTokenCache(): void {
  cachedToken = null
  cachedBriefing = null
}

async function mintStaffToken(): Promise<string> {
  const { data } = await api.get("/api/v1/staff-agent-token")
  if (data?.status !== "success" || !data?.token) {
    throw new Error(data?.message || "Failed to mint staff agent token")
  }
  cachedToken = {
    token: data.token,
    expiresAt: (data.expires_at ?? 0) * 1000
  }
  return data.token
}

// Single-flight: concurrent agent calls on a cold cache (briefing, starters,
// history, events all fire on first open) must share ONE mint — the monolith
// round-trip is the expensive step, and N callers were previously minting N
// times in parallel.
let mintInFlight: Promise<string> | null = null

async function getStaffToken(): Promise<string> {
  if (
    cachedToken &&
    cachedToken.expiresAt - Date.now() > TOKEN_REFRESH_MARGIN_MS
  ) {
    return cachedToken.token
  }
  if (!mintInFlight) {
    mintInFlight = mintStaffToken().finally(() => {
      mintInFlight = null
    })
  }
  return mintInFlight
}

async function agentFetch(
  path: string,
  init: RequestInit = {},
  retryOn401 = true
): Promise<Response> {
  if (!AGENT_URL) {
    throw new Error(
      "Staff agent service is not configured (REACT_APP_ADMIN_AGENT_URL)"
    )
  }
  const token = await getStaffToken()
  const res = await fetch(`${AGENT_URL}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      "x-staff-token": token,
      ...(init.headers ?? {})
    }
  })
  if (res.status === 401 && retryOn401) {
    cachedToken = null
    return agentFetch(path, init, false)
  }
  return res
}

/**
 * POST /api/chat/message/stream — returns the raw Response whose body is an
 * SSE stream (events: text, tool_call, tool_result, done, error). The caller
 * owns reading/parsing (see use-staff-chat-stream).
 */
export async function sendStaffChatMessageStream(
  message: string,
  conversationId: string | undefined,
  page: string | undefined,
  signal: AbortSignal
): Promise<Response> {
  const res = await agentFetch("/api/chat/message/stream", {
    method: "POST",
    body: JSON.stringify({ message, conversationId, page, client: "web" }),
    signal
  })
  if (!res.ok || !res.body) {
    let detail = ""
    try {
      detail = (await res.json())?.error ?? ""
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail || `Agent request failed (${res.status})`)
  }
  return res
}

export interface BriefingItem {
  /**
   * Stable insight kind ("new_leads", "failed_quotes") — the only insight
   * identifier that may enter telemetry. Absent on older agent builds.
   */
  key?: string
  /** One-sentence observation ("14 new leads arrived since Tuesday."). */
  insight: string
  /** Chat message to send verbatim when the user clicks the chip. */
  investigateChip: string
  /** Admin-UI route to open, or null when chat is the venue. */
  platformLink: string | null
}

export interface Briefing {
  generatedAt: string
  since: string
  stage: string | null
  items: BriefingItem[]
}

/**
 * GET /api/briefing — "what's new since you last looked" for the assistant
 * greeting. Reading it advances the server's lastSeenAt, so it's cached for
 * a few minutes: toggling the drawer shouldn't reset the delta window.
 * Identity-scoped like the token cache (cleared together on logout/masquerade).
 */
let cachedBriefing: { briefing: Briefing; fetchedAt: number } | null = null
const BRIEFING_TTL_MS = 5 * 60 * 1000

let briefingInFlight: Promise<Briefing> | null = null

export async function getStaffBriefing(): Promise<Briefing> {
  if (
    cachedBriefing &&
    Date.now() - cachedBriefing.fetchedAt < BRIEFING_TTL_MS
  ) {
    return cachedBriefing.briefing
  }
  // Single-flight: drawer and full page both mount a greeting; only one
  // briefing request should hit the agent per cold cache.
  if (!briefingInFlight) {
    briefingInFlight = (async () => {
      const res = await agentFetch("/api/briefing")
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error(
          data?.error || `Failed to load briefing (${res.status})`
        )
      }
      cachedBriefing = { briefing: data, fetchedAt: Date.now() }
      return data as Briefing
    })().finally(() => {
      briefingInFlight = null
    })
  }
  return briefingInFlight
}

export interface StaffConversationSummary {
  id: string
  title: string
  messageCount: number
  pinned: boolean
  createdAt: string
  updatedAt: string
}

export interface StaffConversationDetail {
  id: string
  messages: {
    role: "user" | "assistant"
    content: string
    turnId?: string
    feedback?: {
      sentiment: "helpful" | "not_helpful"
      reasons: StaffFeedbackReason[]
    }
    /** Typed block parts v1 — approval/form/result/error, restored from history. */
    blocks?: Array<Record<string, any>>
    timestamp?: string
  }[]
  createdAt: string
  updatedAt: string
}

/** Reason codes come from @bb/agent-contract (AWS CodeArtifact). */
export type StaffFeedbackReason = FeedbackReason

export async function submitStaffFeedback(input: {
  /** Defaults to "response". "action" targets use the pending-action id. */
  targetType?: FeedbackTargetType
  targetId: string
  conversationId?: string
  sentiment: "helpful" | "not_helpful"
  reasons?: StaffFeedbackReason[]
  comment?: string
  /** For action targets: what became of the workflow the action was part of. */
  workflowOutcome?: WorkflowOutcome
}): Promise<void> {
  const res = await agentFetch("/api/feedback", {
    method: "POST",
    body: JSON.stringify({
      targetType: "response",
      client: "web",
      ...input
    })
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.error || `Feedback failed (${res.status})`)
  }
}

export async function deleteStaffFeedback(
  targetId: string,
  targetType: FeedbackTargetType = "response"
): Promise<void> {
  const res = await agentFetch(`/api/feedback/${targetType}/${targetId}`, {
    method: "DELETE"
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.error || `Feedback removal failed (${res.status})`)
  }
}

export async function listStaffConversations(): Promise<
  StaffConversationSummary[]
> {
  const res = await agentFetch("/api/conversations")
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(data?.error || `Failed to load history (${res.status})`)
  }
  return data.conversations ?? []
}

export async function getStaffConversation(
  id: string
): Promise<StaffConversationDetail> {
  const res = await agentFetch(`/api/conversations/${id}`)
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(
      data?.error || `Failed to load conversation (${res.status})`
    )
  }
  return data.conversation
}

export async function pinStaffConversation(
  id: string,
  pinned: boolean
): Promise<void> {
  const res = await agentFetch(
    `/api/conversations/${id}/${pinned ? "pin" : "unpin"}`,
    { method: "POST" }
  )
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(
      data?.error || `Failed to ${pinned ? "pin" : "unpin"} (${res.status})`
    )
  }
}

export async function deleteStaffConversation(id: string): Promise<void> {
  const res = await agentFetch(`/api/conversations/${id}`, {
    method: "DELETE"
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(
      data?.error || `Failed to delete conversation (${res.status})`
    )
  }
}

export interface PendingActionResult {
  status: string
  result?: unknown
  error?: string
}

export async function getPendingAction(id: string): Promise<StaffActionDetail> {
  const res = await agentFetch(`/api/actions/${id}`)
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(data?.error || `Action status failed (${res.status})`)
  }
  return data.action
}

export interface StaffActionDetail extends PendingActionResult {
  id: string
  actionType: string
  summary: string
  payload?: Record<string, unknown>
  createdAt: string
  executionStartedAt?: string
  executionAttempts?: number
}

export class PendingActionRequestError extends Error {
  actionStatus?: string

  constructor(message: string, actionStatus?: string) {
    super(message)
    this.name = "PendingActionRequestError"
    this.actionStatus = actionStatus
  }
}

export async function approvePendingAction(
  id: string
): Promise<PendingActionResult> {
  const res = await agentFetch(`/api/actions/${id}/approve`, { method: "POST" })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new PendingActionRequestError(
      data?.error || `Approve failed (${res.status})`,
      data?.status
    )
  }
  return data
}

export async function rejectPendingAction(
  id: string
): Promise<PendingActionResult> {
  const res = await agentFetch(`/api/actions/${id}/reject`, { method: "POST" })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new PendingActionRequestError(
      data?.error || `Reject failed (${res.status})`,
      data?.status
    )
  }
  return data
}

/**
 * Assistant funnel events (shared web/mobile taxonomy — see
 * ASSISTANT_FUNNEL_EVENTS on the agent service). Fire-and-forget transport
 * for the batching tracker in common/utils/assistant-analytics.ts; keepalive
 * lets a final flush survive page unload.
 */
export interface AssistantFunnelEventInput {
  event: string
  fields?: Record<string, string | number | boolean>
}

export async function postStaffAssistantEvents(
  events: AssistantFunnelEventInput[]
): Promise<void> {
  const res = await agentFetch("/api/events", {
    method: "POST",
    body: JSON.stringify({ client: "web", events }),
    keepalive: true
  })
  if (!res.ok) throw new Error(`Failed to record events (${res.status})`)
}

export interface ConversationStarter {
  label: string
  message: string
}

const startersCache = new Map<string, ConversationStarter[]>()

/**
 * Context-aware starter chips for the admin route behind the assistant.
 * Deterministic server-side catalog; cached per path for the session.
 */
export async function getStaffStarters(
  page?: string
): Promise<ConversationStarter[]> {
  // No page -> the server returns its generic starter set.
  const path = page ? page.split("?")[0] : ""

  const cached = startersCache.get(path)
  if (cached) return cached
  const res = await agentFetch(`/api/starters?page=${encodeURIComponent(path)}`)
  if (!res.ok) return []
  const data = await res.json().catch(() => ({}))
  const starters: ConversationStarter[] = Array.isArray(data?.starters)
    ? data.starters
    : []
  startersCache.set(path, starters)
  return starters
}

export interface TopicsPeek {
  hasNew: boolean
  count: number
  topics: string[]
}

/**
 * Read-only peek at whether there are new topics to explore — used to badge
 * the assistant entry point on app load. Never acknowledges topics (only
 * opening the assistant and reading the briefing does).
 */
export async function getStaffTopicsPeek(): Promise<TopicsPeek> {
  const res = await agentFetch("/api/briefing/topics")
  if (!res.ok) return { hasNew: false, count: 0, topics: [] }
  const data = await res.json().catch(() => ({}))
  return {
    hasNew: Boolean(data?.hasNew),
    count: Number(data?.count) || 0,
    topics: Array.isArray(data?.topics) ? data.topics : []
  }
}

export interface StaffAgentConfig {
  features: Record<string, boolean>
  contractVersion?: string
}

let cachedConfig: StaffAgentConfig | null = null

/**
 * Client bootstrap config: per-user feature flags + contract version.
 * Cached for the session; failures resolve to no features (flags are
 * deny-by-default end to end).
 */
export async function getStaffConfig(): Promise<StaffAgentConfig> {
  if (cachedConfig) return cachedConfig
  try {
    const res = await agentFetch("/api/config")
    if (!res.ok) return { features: {} }
    const data = await res.json().catch(() => ({}))
    cachedConfig = {
      features:
        data?.features && typeof data.features === "object"
          ? data.features
          : {},
      contractVersion: data?.contractVersion
    }
    return cachedConfig
  } catch {
    return { features: {} }
  }
}
